import AddItemDialog, {
  AddItemType,
} from "@/components/organism/AddItemDialog";
import { ReactComponent as AddSvg } from "@/src/assets/icons/icon-add-black.svg";
import AddBlueSvg from "@/src/assets/icons/icon-add-blue.svg";
import ChevronLeftSvg from "@/src/assets/icons/icon-chevron-left-black.svg";
import InfoSvg from "@/src/assets/icons/icon-info.svg";
import PreviewSvg from "@/src/assets/icons/icon-preview-black.svg";
import AlertDialog from "@/src/components/atom/AlertDialog";
import { Button } from "@/src/components/atom/Button";
import SelectOptionButton from "@/src/components/atom/Button/SelectOptionButton";
import Icon from "@/src/components/atom/Icon";
import Input from "@/src/components/atom/Input";
import Loader from "@/src/components/atom/Loader";
import Select from "@/src/components/atom/Select";
import Typo from "@/src/components/atom/Typo";
import BottomFixedContainer from "@/src/components/molecule/BottomFixedContainer";
import BreakWordTypo from "@/src/components/molecule/BreakWordTypo";
import Flex from "@/src/components/molecule/Flex";
import FormItem, { InputError } from "@/src/components/molecule/FormItem";
import SectionCard from "@/src/components/molecule/SectionCard";
import SectionCardGrid from "@/src/components/molecule/SectionCardGrid";
import SectionCardRow from "@/src/components/molecule/SectionCardRow";
import NoRegisteredSignatureAlertDialog from "@/src/components/organism/NoRegisteredSignatureAlertDialog";
import ReferenceImageDialog from "@/src/components/organism/ReferenceImageDialog";
import RegisterCompanySignDialog from "@/src/components/organism/RegisterCompanySignDialog";
import SignatureRequestAlertDialog from "@/src/components/organism/SignatureRequestAlertDialog";
import TermsAddDialog from "@/src/components/organism/TermsAddDialog";
import ExporterMainLayout from "@/src/components/template/Layout/exporter/ExporterMainLayout";
import { DIRECT_INPUT_LABEL, DIRECT_INPUT_VALUE } from "@/src/constant";
import DATE_FORMAT_STRINGS from "@/src/constant/dateFormat";
import useAlert from "@/src/hooks/useAlert";
import useContentLoading from "@/src/hooks/useContentLoading";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import { useAppSelector } from "@/src/store";
import { useGetSessionQuery } from "@/src/store/apis/auth";
import {
  useCreateBuyerEmployeesMutation,
  useGetBuyersQuery,
  useLazyGetBuyerEmployeesQuery,
} from "@/src/store/apis/client/buyer";
import { GenerateBuyerEmployeeDto } from "@/src/store/apis/client/buyer/interface";
import { useGetCommonCodeListQuery } from "@/src/store/apis/common";
import {
  useCreateContractBasedPoMutation,
  useLazyCheckScNoQuery,
} from "@/src/store/apis/contracts";
import { useLazyGetContractDetailQuery } from "@/src/store/apis/contracts/contractDetail";
import { useGetExportersQuery } from "@/src/store/apis/corporate/company";
import {
  useCreateSingleItemCodeMutation,
  useGetNonFilteredItemCodesQuery,
} from "@/src/store/apis/corporate/companyItemManagement";
import { ExporterItemCodeListViewV2Dto } from "@/src/store/apis/corporate/companyItemManagement/interface";
import colorSet from "@/src/styles/color";
import { mediaQuery } from "@/src/styles/mediaQuery";
import typo from "@/src/styles/typography";
import { commonCodeMapper } from "@/src/utils/commonCodeHelper";
import { isNull, isUndefined } from "@/src/utils/is";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled, { css } from "styled-components";
import { GroupType, OptionType } from "types";
import BuyerEmployeeAddDialog from "../../../ClientManagement/dialog/BuyerEmployeeAddDialog";
import { ContractAddFormType } from "../../types/contract.type";
import { contractAddBasedOnPoForm } from "../../utils/defaultValue";
import SignatureSelectDialog from "@/src/components/organism/SignatureSelectDialog";
import { scPreview } from "@/src/components/template/pdfs/Sc/ScContent";
import { ScData } from "@/src/components/template/pdfs/Sc/types";
import DatePickerNoticeTypo from "@/src/components/molecule/DatePickerNoticeTypo";
import { useGetCreditStatusInfoQuery } from "@/src/store/apis/credit";

enum DialogState {
  NULL,
  IMAGE,
  LOAD_CONTRACT,
  ADD_ITEM,
  ADD_CONTACT_PERSON,
  CREATE_TERMS,
  REGISTER_SIGN,
}

enum AlertDialogState {
  NULL,
  SAVED_DATA,
  BACK_TO_PREVIOUS,
  REGISTER_SIGN,
  SIGNATURE_REQUEST,
  SEND_TO_BUYER,
  SIGNATURE_SELECT_SC,
}

interface ContractAddBasedOnPoPageProps {
  id: number;
}

const ContractAddBasedOnPoPage = ({ id }: ContractAddBasedOnPoPageProps) => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const alert = useAlert();
  const { handleContentLoadingOff, handleContentLoadingOn } =
    useContentLoading();

  const exportersData = useGetExportersQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  useGetCreditStatusInfoQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const isCompanyTerms =
    !!exportersData.currentData?.termsOfSalesContract
      .salesContractTermsWithLatestHistory;

  const isTwoSign =
    exportersData.currentData?.signatureManagement
      ?.simpleExporterSignatureMedias?.length === 2;

  const defaultSignatureMediaId =
    exportersData?.currentData?.signatureManagement?.simpleExporterSignatureMedias?.find(
      (item) => item.isDefault
    )?.signatureSimpleMedia.id as number;

  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
    handleSubmit,
    getValues,
    clearErrors,
    reset,
    unregister,
  } = useForm<ContractAddFormType>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      ...contractAddBasedOnPoForm,
      isAddContractTerms: isCompanyTerms ? ["true"] : [],
    },
  });

  const user = useAppSelector((state) => state.auth.user);
  const subscription = useAppSelector((state) => state.subscription);

  const numberOfUsers =
    (subscription.subscriptionCredit?.numberOfMember as number) -
      (subscription.subscriptionCredit?.memberNumberCredit as number) || 0;

  const isCreateDisabled =
    user?.exporterUserMainFieldType === "BUYER" ||
    !subscription.isCompanySubscription ||
    (subscription?.subscriptionCredit?.numberOfMember || 0) < numberOfUsers;

  const [
    getContractDetail,
    {
      quantityAndUnit,
      unitPriceAndUnit,
      amount,
      currentData,
      mainCategoryForImporter,
      importerItem,
      hasLastShipmentDateAtOnPo,
      hasFreetimeOnPo,
      hasOriginOnPo,
    },
  ] = useLazyGetContractDetailQuery({
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isUnstable = isUndefined(currentData) || isError || isFetching;

      return {
        quantityAndUnit:
          !isUnstable && currentData.quantity && currentData.quantityUnit
            ? `${Number(currentData.quantity).toLocaleString("ko-KR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} ${currentData.quantityUnit}`
            : "-",
        unitPriceAndUnit:
          !isUnstable && currentData.unitPrice && currentData.unitPriceUnit
            ? `${Number(currentData.unitPrice).toLocaleString("ko-KR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} ${currentData.unitPriceUnit}`
            : "-",
        amount:
          !isUnstable &&
          currentData.quantity &&
          currentData.unitPrice &&
          currentData.unitPriceUnit
            ? `${parseFloat(
                (
                  Number(currentData.quantity) * Number(currentData.unitPrice)
                ).toFixed(2)
              ).toLocaleString("ko-KR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} ${currentData.unitPriceUnit}`
            : "-",
        currentData,
        importerItem: !isUnstable ? currentData.importerItem : null,
        hasLastShipmentDateAtOnPo: !isUnstable
          ? !!currentData.lastShipmentDateAt
          : true,
        hasFreetimeOnPo: !isUnstable ? !!currentData.freetime : true,
        hasOriginOnPo: !isUnstable ? !!currentData.origin : true,
        mainCategoryForImporter: !isUnstable
          ? currentData.mainCategoryForImporter
          : null,
      };
    },
  });

  const isVisibleTransactionInformation =
    !hasLastShipmentDateAtOnPo || !hasFreetimeOnPo || !hasOriginOnPo;
  const [selectImage, setSelectImage] = useState("");
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.NULL);
  const [alertDialogState, setAlertDialogState] = useState<AlertDialogState>(
    AlertDialogState.NULL
  );

  // SC PDF
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const previewButtonRef = useRef<HTMLButtonElement>(null);
  const previewScPdfResourceRef = useRef<ContractAddFormType | null>(null);

  const { mainCategoryList, exporterUserType } = useGetSessionQuery(undefined, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isErrorAndUndefined =
        isError || isFetching || currentData === undefined;

      const isManager =
        currentData?.row.exporterUserType === "MANAGER" ||
        currentData?.row.exporterUserType === "MIDDLE_MANAGER";

      return {
        mainCategoryList: !isErrorAndUndefined
          ? isManager
            ? currentData.row.mainCategoryCodeItemNames
            : currentData.row.exporter.mainCategoryCodeItemNames
          : [],
        exporterUserType: currentData?.row.exporterUserType,
      };
    },
  });
  /**
   * RTK Query상 Create지만 실제 데이터상으로는 PO기반의 SC수정
   */
  const [createContract] = useCreateContractBasedPoMutation();
  const [createEmployee] = useCreateBuyerEmployeesMutation();
  const [checkScNo] = useLazyCheckScNoQuery();

  const { buyerList, isBuyersFetching } = useGetBuyersQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isFetching, isError }) => {
        const isUnstable = isUndefined(currentData) || isFetching || isError;

        return {
          buyerList: !isUnstable ? currentData.data : [],
          isBuyersFetching: isFetching,
        };
      },
    }
  );
  const [createSingleItemCode] = useCreateSingleItemCodeMutation();
  const [getBuyerEmployees, buyerEmployeesResult] =
    useLazyGetBuyerEmployeesQuery();
  const {
    itemsList,
    isItemsFetching,
    refetch: itemListRefetch,
  } = useGetNonFilteredItemCodesQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isFetching, isError }) => {
        const isUnstable = isUndefined(currentData) || isFetching || isError;
        const isStable = !isUnstable;

        return {
          itemsList: isStable ? currentData.rows : [],
          isItemsFetching: isFetching,
        };
      },
    }
  );

  const { isCommonCodeFetching, commonCodeCurrentData } =
    useGetCommonCodeListQuery(
      {
        codeNameList: [
          { codeName: "FREETIME" },
          { codeName: "COUNTRY" },
          { codeName: "MAIN_CATEGORY" },
        ],
      },
      {
        refetchOnMountOrArgChange: true,
        selectFromResult: ({ isFetching, currentData }) => {
          return {
            isCommonCodeFetching: isFetching,
            commonCodeCurrentData: currentData ?? [],
          };
        },
      }
    );

  const [freeTimeList = [], countryList = [], commonMainCategoryList = []] =
    commonCodeCurrentData;

  const buyerToOptionList = buyerList
    .filter(
      ({ isActivated, mainCategoryCodeItemNames }) =>
        isActivated &&
        mainCategoryCodeItemNames.some((mainCategory) =>
          mainCategoryList.includes(mainCategory)
        )
    )
    .reduce<OptionType[]>((acc, val) => {
      const resource = {
        label: val.buyerName,
        value: val.buyerListQueryResultDto.id.toString(),
      };
      return [...acc, resource];
    }, []);

  const buyerListToOptionList = buyerEmployeesResult.isError
    ? []
    : buyerEmployeesResult.currentData?.rows
        .filter(({ isActivated }) => isActivated)
        .reduce<OptionType[]>((acc, val) => {
          const resource = {
            label: val.name,
            value: val.id.toString(),
          };
          return [...acc, resource];
        }, []);

  const buyerMainCategory =
    buyerList.find(({ id }) => currentData?.buyerId === id)
      ?.mainCategoryCodeItemNames ?? [];

  const itemListToGroupedOptionList = itemsList
    .filter(({ isActivated, mainCategoryCodeItemName, mainCategory }) => {
      return (
        isActivated &&
        mainCategoryList.includes(mainCategoryCodeItemName) &&
        buyerMainCategory.includes(mainCategoryCodeItemName) &&
        mainCategory === mainCategoryForImporter
      );
    })
    .reduce((acc: GroupType[], item: ExporterItemCodeListViewV2Dto) => {
      const groupLabel = `${item.mainCategory} > ${item.subCategory}`;

      // 그룹이 이미 존재하는지 확인
      let group = acc.find((g) => g.label === groupLabel);
      if (!group) {
        group = { label: groupLabel, options: [] };
        acc.push(group);
      }

      // 옵션 레이블 생성
      const isItemCodeSameAsItem = item.item === item.itemCode;
      const optionLabel = isItemCodeSameAsItem
        ? `${item.item}`
        : `${item.item}(${item.itemCode})`;

      // value 필드 수정
      const optionValue = `${item.id}_${item.exporterItemId}`;

      // 옵션 추가
      group.options.push({
        label: optionLabel,
        value: optionValue,
      });

      return acc;
    }, []);

  const freetimeOptions = freeTimeList.reduce<OptionType[]>(
    commonCodeMapper,
    []
  );

  const referenceImageList =
    getValues("referenceMediaIdList")?.map((item) => item.mediaUrl) || [];

  const selectBuyer = buyerEmployeesResult
    ? buyerEmployeesResult?.currentData?.rows.filter(
        (item) => item.id === Number(watch("buyerEmployeeId"))
      )[0]?.buyer
    : null;

  const bankAddressData = {
    bankCountryName:
      exportersData?.currentData?.exporterDetailQueryResultDto.bankCountryName,
    bankPostalCode:
      exportersData?.currentData?.exporterDetailQueryResultDto.bankPostalCode,
    bankRegion:
      exportersData?.currentData?.exporterDetailQueryResultDto.bankRegion,
    bankLocality:
      exportersData?.currentData?.exporterDetailQueryResultDto.bankLocality,
    bankStreetAddress:
      exportersData?.currentData?.exporterDetailQueryResultDto
        .bankStreetAddress,
  };

  const bankAddress = [
    bankAddressData.bankStreetAddress,
    bankAddressData.bankRegion,
    bankAddressData.bankLocality,
    bankAddressData.bankPostalCode,
    bankAddressData.bankCountryName,
  ]
    .filter(Boolean)
    .join(",");

  const handlePreview = () => {
    const scData: ScData = {
      scNumber: previewScPdfResourceRef.current?.scNo || "",
      componyLogoUrl:
        exportersData.currentData?.basicInformation?.businessLogoSimpleMedia
          ?.mediaUrl || "",
      sellerCorporation:
        exportersData.currentData?.basicInformation?.companyName || "",
      sellerContact:
        exportersData.currentData?.exporterDetailQueryResultDto?.tel || "",
      sellerContactPrefix:
        exportersData.currentData?.exporterDetailQueryResultDto?.telPrefix ||
        "",
      sellerFax:
        exportersData?.currentData?.exporterDetailQueryResultDto?.fax || "",
      sellerFaxPrefix:
        exportersData?.currentData?.exporterDetailQueryResultDto?.faxPrefix ||
        "",
      sellerCountryName:
        exportersData?.currentData?.exporterDetailQueryResultDto?.countryName ||
        "",
      sellerRegion:
        exportersData?.currentData?.exporterDetailQueryResultDto?.region || "",
      sellerPostalCode:
        exportersData?.currentData?.exporterDetailQueryResultDto?.postalCode ||
        "",
      sellerLocality:
        exportersData?.currentData?.exporterDetailQueryResultDto?.locality ||
        "",
      sellerStreetAddress:
        exportersData?.currentData?.exporterDetailQueryResultDto
          ?.streetAddress || "",

      buyerCorporation: selectBuyer ? selectBuyer?.buyerName : "",
      buyerContactPrefix: selectBuyer ? selectBuyer?.telPrefix : "",
      buyerContact: selectBuyer ? selectBuyer?.tel : "",
      buyerFaxPrefix: selectBuyer ? selectBuyer?.faxPrefix : "",
      buyerFax: selectBuyer ? selectBuyer?.fax : "",
      buyerCountryName: selectBuyer ? selectBuyer?.buyerCountryName : "",
      buyerRegion: selectBuyer ? selectBuyer?.buyerRegion : "",
      buyerPostalCode: selectBuyer ? selectBuyer?.buyerPostalCode : "",
      buyerLocality: selectBuyer ? selectBuyer?.buyerLocality : "",
      buyerStreetAddress: selectBuyer
        ? selectBuyer?.buyerStreetAddress || ""
        : "",

      orderDate: previewScPdfResourceRef.current?.orderDateAt
        ? dayjs(previewScPdfResourceRef.current?.orderDateAt).format(
            DATE_FORMAT_STRINGS.YYYY_MM_DD
          )
        : "",
      paymentTerm:
        previewScPdfResourceRef.current?.paymentTerms ||
        currentData?.paymentTerms ||
        "",
      paymentTermsRemark:
        previewScPdfResourceRef.current?.paymentTermsRemark ||
        currentData?.paymentTermsRemark ||
        "",
      originLocation:
        previewScPdfResourceRef.current?.origin || currentData?.origin || "",
      shippingTerm:
        previewScPdfResourceRef.current?.shippingTerms ||
        currentData?.shippingTerms ||
        "",
      shippingTermRemark:
        previewScPdfResourceRef.current?.shippingTermsRemark ||
        currentData?.shippingTermsRemark ||
        "",
      lastShipmentDate:
        dayjs(
          currentData?.lastShipmentDateAt
            ? currentData?.lastShipmentDateAt
            : previewScPdfResourceRef.current?.lastShipmentDateAt
        ).format(DATE_FORMAT_STRINGS.YYYY_MM_DD) || "",
      quantityPrefix: currentData?.quantityUnit || "",
      quantity: currentData?.quantity
        ? Number(
            currentData?.quantity.toString().replace(/[,]/g, "")
          ).toLocaleString("ko-KR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : "",
      description: previewScPdfResourceRef.current?.item || "",
      hsCode: previewScPdfResourceRef.current?.hsCode || "",
      unitPricePrefix: currentData?.unitPriceUnit || "",
      unitPrice: currentData?.unitPrice
        ? Number(
            currentData?.unitPrice.toString().replace(/[,]/g, "")
          ).toLocaleString("ko-KR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : "",
      amount:
        (currentData?.quantity &&
          currentData?.unitPrice &&
          Number(
            (
              (parseFloat(currentData?.quantity.toString().replace(/,/g, "")) ||
                0) *
              (parseFloat(
                currentData?.unitPrice.toString().replace(/,/g, "")
              ) || 0)
            ).toFixed(2)
          ).toLocaleString("ko-KR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })) ||
        "",
      remark: previewScPdfResourceRef.current?.contractRemark || "",

      isAddBankDetail:
        !!previewScPdfResourceRef.current?.isAddBankDetail.length,
      isAddContractTerms:
        !!previewScPdfResourceRef.current?.isAddContractTerms.length,
      contractTermsTitle:
        exportersData.currentData?.termsOfSalesContract
          ?.salesContractTermsWithLatestHistory?.title || "",
      contractTerms:
        exportersData.currentData?.termsOfSalesContract
          .salesContractTermsWithLatestHistory?.latestSalesContractTermsHistory
          .body,
      bankName:
        exportersData?.currentData?.exporterDetailQueryResultDto?.bankName ||
        "",
      bankTelPrefix:
        exportersData?.currentData?.exporterDetailQueryResultDto
          ?.bankTelPrefix || "",
      bankTel:
        exportersData?.currentData?.exporterDetailQueryResultDto?.bankTel || "",
      bankFaxPrefix:
        exportersData?.currentData?.exporterDetailQueryResultDto?.faxPrefix ||
        "",
      bankFax:
        exportersData?.currentData?.exporterDetailQueryResultDto?.bankFax || "",
      bankStreetAddress: bankAddress || "",
      bankLocality:
        exportersData?.currentData?.exporterDetailQueryResultDto
          ?.bankLocality || "",
      bankRegion:
        exportersData?.currentData?.exporterDetailQueryResultDto?.bankRegion ||
        "",
      bankPostalCode:
        exportersData?.currentData?.exporterDetailQueryResultDto
          ?.bankPostalCode || "",
      bankCountryName:
        exportersData?.currentData?.exporterDetailQueryResultDto
          ?.bankCountryName || "",
      swiftCode:
        exportersData?.currentData?.exporterDetailQueryResultDto?.swiftCode ||
        "",
      accountNumber:
        exportersData?.currentData?.exporterDetailQueryResultDto
          ?.accountNumber || "",
      accountName:
        exportersData?.currentData?.exporterDetailQueryResultDto?.accountName ||
        "",
      sellerSignatureUrl:
        exportersData?.currentData?.signatureManagement?.simpleExporterSignatureMedias?.find(
          (item) => item.isDefault
        )?.signatureSimpleMedia.mediaUrl || "",
      buyerSignatureUrl: "",
    };

    scPreview(scData);
  };

  const handleImageClick = (image: string) => {
    setDialogState(DialogState.IMAGE);
    setSelectImage(image);
  };

  const handleCreateContract = async () => {
    /**
     * - 기업관리자, 중간관리자 서명등록
     * - 일반 매니저 기업관리자에게 서명요청
     */
    if (
      isNull(
        exportersData.currentData?.signatureManagement
          .simpleExporterSignatureMedias
      )
    ) {
      if (exporterUserType !== "MANAGER") {
        return setAlertDialogState(AlertDialogState.REGISTER_SIGN);
      } else {
        return setAlertDialogState(AlertDialogState.SIGNATURE_REQUEST);
      }
    }
    /**
     * - 서명이 2개일 경우 서명선택모달
     * - 서명이 1개일 경우 default
     */
    isTwoSign
      ? setAlertDialogState(AlertDialogState.SIGNATURE_SELECT_SC)
      : createScContract(getValues(), defaultSignatureMediaId);
  };

  const createScContract = async (
    formValue: ContractAddFormType,
    selectSignatureMediaId?: number
  ) => {
    try {
      handleContentLoadingOn();

      const { row } = await createContract({
        /**
         * Basic Information
         */
        id,
        scNo: formValue.scNo,
        buyerId: Number(formValue.buyerId),
        buyerEmployeeId: Number(formValue.buyerEmployeeId),
        allocation: formValue.allocation || undefined,
        orderDateAt: dayjs(formValue.orderDateAt).startOf("day").toISOString(),
        isAddContractTerms: !!formValue.isAddContractTerms.length,
        hsCode: formValue.hsCode || undefined,
        exporterItemId: Number(formValue.exporterItemId.split("_")[1]),
        exporterItemCodeId: Number(formValue.exporterItemCodeId),
        /**
         * Transaction Information
         */
        freetime:
          formValue.freetimeCodeItemName === DIRECT_INPUT_VALUE
            ? formValue.freetimeDirect || undefined
            : formValue.freetime || undefined,
        freetimeCodeItemName:
          formValue.freetimeCodeItemName === DIRECT_INPUT_VALUE
            ? undefined
            : formValue.freetimeCodeItemName || undefined,
        isAddBankDetail: !!formValue.isAddBankDetail.length,
        lastShipmentDateAt: formValue.lastShipmentDateAt
          ? dayjs(formValue.lastShipmentDateAt).startOf("day").toISOString()
          : undefined,
        /**
         * Attachment file
         */
        referenceMediaIdList: formValue.referenceMediaIdList.length
          ? formValue.referenceMediaIdList.map(({ id }) => id)
          : undefined,
        scMediaId: formValue?.scMediaId?.[0]?.id,
        contractRemark: formValue.contractRemark || undefined,
        isSendToBuyer: true,
        signatureMediaId: selectSignatureMediaId
          ? selectSignatureMediaId
          : defaultSignatureMediaId,
      }).unwrap();

      alert.showAlert({
        type: "success",
        message: t("contract:exporter.add.alert.saveSuccess"),
      });
      if (row.id) {
        navigate(`${EXPORTER_PRIVATE_PATH.CONTRACT_DETAIL}/${row.id}`);
      }
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    } finally {
      handleContentLoadingOff();
    }
  };

  const handleAddItems = async ({
    item,
    exporterItemCodeList,
    subCategoryCodeItemName,
    mainCategoryCodeItemName,
  }: AddItemType) => {
    const buyerMainCategoryCodeItemName = commonMainCategoryList.find(
      ({ codeItemNameEn }) => codeItemNameEn === mainCategoryForImporter
    )?.codeItemName;

    if (buyerMainCategoryCodeItemName !== mainCategoryCodeItemName) {
      return alert.showAlert({
        type: "error",
        message: t("alert:importerContractCategoryMismatch"),
      });
    }

    try {
      const { row } = await createSingleItemCode({
        item: item,
        itemCode: exporterItemCodeList[0].itemCode,
        subCategoryCodeItemName: subCategoryCodeItemName,
        mainCategoryCodeItemName: mainCategoryCodeItemName,
      }).unwrap();

      itemListRefetch();

      const categoryList =
        buyerList.find(({ id }) => watch("buyerId") === id.toString())
          ?.mainCategoryCodeItemNames ?? [];

      if (categoryList.includes(mainCategoryCodeItemName)) {
        setValue("exporterItemId", `${row.id}_${row.exporterItemId}`);
        setValue("mainCategory", row.mainCategory ?? "");
        setValue("subCategory", row.subCategory ?? "");
        setValue("item", row.item ?? "");
        setValue("exporterItemCodeId", String(row.id));
        setValue("exporterItemCode", row.itemCode);
        clearErrors("exporterItemId");
      }

      alert.showAlert({
        type: "success",
        message: t("alert:saveSuccess"),
      });

      setDialogState(DialogState.NULL);
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const handleAddBuyerEmployee = async (
    values: Omit<GenerateBuyerEmployeeDto, "isActivated"> & {
      isActivated: string;
    }
  ) => {
    const buyerId = getValues("buyerId");

    try {
      setDialogState(DialogState.NULL);
      const { row: createdEmployee } = await createEmployee({
        id: Number(buyerId),
        name: values.name,
        email: values.email,
        isActivated: values.isActivated === "Y",
        personalContactPrefix: values.personalContactPrefix || undefined,
        personalContact: values.personalContact || undefined,
        officeContactPrefix: values.officeContactPrefix || undefined,
        officeContact: values.officeContact || undefined,
        buyerEmployeeRemark: values.buyerEmployeeRemark || undefined,
      }).unwrap();

      await getBuyerEmployees({
        id: Number(buyerId),
      });

      setValue("buyerEmployeeId", createdEmployee.id.toString());
      clearErrors("buyerEmployeeId");
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    } finally {
    }
  };

  // 아이템 코드
  const renderItemSubInputs = () => {
    return (
      <>
        <SubFormRow>
          <SubFormLabel>
            {t("contract:exporter.add.common.mainCategory")}
          </SubFormLabel>
          <Controller
            name="mainCategory"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                style={{ width: "100%" }}
                disabled
                placeholder={t("common:mainCategory")}
                value={
                  getValues("mainCategory")
                    ? `${field.value} > ${watch("subCategory")}`
                    : "-"
                }
              />
            )}
          />
        </SubFormRow>
        <SubFormRow>
          <SubFormLabel>
            {t("contract:exporter.add.common.itemCode")}
          </SubFormLabel>
          <Controller
            name="exporterItemCode"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                style={{ width: "100%" }}
                disabled
                placeholder={
                  getValues("mainCategory") ? "-" : t("common:itemCode")
                }
              />
            )}
          />
        </SubFormRow>
      </>
    );
  };

  const renderContractInfoBasedPo = () => {
    return (
      <>
        <SectionCardGrid>
          {[
            {
              gridColumn: 6,
              title: t("common:buyer"),
              value: (
                <BuyerText
                  color="systemBlue2"
                  typoType="h4"
                  onClick={() =>
                    navigate(
                      `${EXPORTER_PRIVATE_PATH.CLIENT_MANAGEMENT}/${currentData?.buyerId}`
                    )
                  }
                >
                  {currentData?.buyerName || "-"}
                </BuyerText>
              ),
            },
            {
              gridColumn: 3,
              title: t("common:shippingTerms"),
              value: (
                <BreakWordTypo typoType="h4">
                  {currentData?.shippingTerms || "-"}{" "}
                  {currentData?.shippingTermsRemark || ""}
                </BreakWordTypo>
              ),
            },
            {
              gridColumn: 3,
              title: t("common:buyerContact"),
              value: <BreakWordTypo typoType="h4">-</BreakWordTypo>,
            },
            {
              isDivider: true,
            },
            {
              gridColumn: 3,
              title: t("common:item"),
              value: (
                <BreakWordTypo typoType="h4">{importerItem}</BreakWordTypo>
              ),
            },
            {
              gridColumn: 3,
              title: t("common:qty"),
              value: (
                <BreakWordTypo typoType="h4">{quantityAndUnit}</BreakWordTypo>
              ),
            },
            {
              gridColumn: 3,
              title: t("common:unitPrice"),
              value: (
                <BreakWordTypo typoType="h4">{unitPriceAndUnit}</BreakWordTypo>
              ),
            },
            {
              gridColumn: 3,
              title: t("common:amount"),
              value: <BreakWordTypo typoType="h4">{amount}</BreakWordTypo>,
            },
          ].map(({ gridColumn, isDivider = false, title, value }) => {
            if (isDivider) {
              return <StyledSectionCardRow data-columns={12} data-divider />;
            }
            return (
              <StyledSectionCardRow
                data-columns={gridColumn}
                label={
                  <Typo color="gray6" typoType="b9m">
                    {title}
                  </Typo>
                }
                direction="vertical"
                value={value}
              />
            );
          })}
        </SectionCardGrid>

        <BottomSectionCard>
          {[
            {
              gridColumn: 3,
              title: t("common:placeOfDelivery"),
              value: currentData?.placeOfDelivery || "-",
            },
            {
              gridColumn: 3,
              title: t("common:portOfDischarge"),
              value: currentData?.portOfDischarge || "-",
            },
            {
              gridColumn: 3,
              title: t("common:paymentTerms"),
              value: `${currentData?.paymentTerms} ${
                currentData?.paymentTermsRemark || ""
              }`,
            },
            {
              gridColumn: 3,
              title: t("common:originLocation"),
              value: currentData?.origin || "-",
            },
            {
              isDivider: true,
            },
            {
              gridColumn: 4,
              title: t("common:lastShipmentDate"),
              value: currentData?.lastShipmentDateAt
                ? dayjs(currentData?.lastShipmentDateAt).format(
                    DATE_FORMAT_STRINGS.YYYY_MM_DD
                  )
                : "-",
            },
            {
              gridColumn: 4,
              title: t("common:freetime"),
              value: currentData?.freetime || "-",
            },
            {
              gridColumn: 4,
              title: t("common:deliveryDate"),
              value: currentData?.deliveryDateAt
                ? dayjs(currentData.deliveryDateAt).format(
                    DATE_FORMAT_STRINGS.YYYY_MM_DD
                  )
                : "-",
            },
          ].map(({ gridColumn, isDivider = false, title, value }) => {
            if (isDivider) {
              return <StyledSectionCardRow data-columns={12} data-divider />;
            }
            return (
              <StyledSectionCardCenterRow
                data-columns={gridColumn}
                label={
                  <Typo color="gray6" typoType="b9m">
                    {title}
                  </Typo>
                }
                direction="vertical"
                value={
                  <Typo typoType="h4" color="blue2" className="ant-typography">
                    {value}
                  </Typo>
                }
              />
            );
          })}
        </BottomSectionCard>
      </>
    );
  };

  // 추가 입력 정보 Basic Information, Transaction Information
  const renderAdditionalInfoBasedPo = () => {
    // 선택사항
    const optionalItemValue = () => {
      if (exportersData.isFetching) {
        return <Loader />;
      }

      // 약관이 있는경우에만 체크박스
      if (isCompanyTerms) {
        return (
          <StyledFormItem
            type="checkbox"
            control={control as any}
            name="isAddContractTerms"
            options={[
              {
                label: t("contract:exporter.add.common.addContractTerm"),
                value: "true",
              },
            ]}
          />
        );
      }

      return (
        <Flex gap={16} alignItems="center" flexWrap="wrap">
          <Typo typoType="b7r" color="gray7">
            {t("common:noRegisteredContractTerms")}
          </Typo>
          <Button
            buttonGrade="tertiary"
            buttonColor="black"
            buttonSize={32}
            onClick={() => setDialogState(DialogState.CREATE_TERMS)}
          >
            <Flex alignItems="center" gap={4}>
              <AddIcon />
              {t("common:registerContractTerms")}
            </Flex>
          </Button>
        </Flex>
      );
    };

    return (
      <>
        <SectionCard
          cardTitle={t(
            "contract:exporter.add.common.additionalInputInformation"
          )}
          cardContentContainerStyle={{ display: "flex", gap: "24px" }}
        >
          <FlexColumn flexGap={16}>
            <Typo typoType="b9m" color="gray7">
              {t("contract:exporter.add.common.basicInformation")}
            </Typo>

            <FlexRow>
              <HalfFormItemContainer>
                <StyledFormItem
                  label={t("contract:exporter.add.common.scNo")}
                  type={"text"}
                  name={"scNo"}
                  control={control as any}
                  inputProps={{
                    placeholder: t("contract:exporter.add.placeholder.scNo"),
                  }}
                  rules={{
                    maxLength: 50,
                    required: true,
                    validate: async (scNo) => {
                      // 초기값과 현재 입력값이 같으면 검증 통과
                      if (
                        searchParams.get("clientCheck") === "false" &&
                        scNo === currentData?.scNo
                      ) {
                        return true;
                      }
                      // 다른 경우에만 중복 체크 실행
                      if (scNo.length >= 50) return true;
                      const checkResult = await checkScNo({ scNo });
                      return !checkResult.isError;
                    },
                  }}
                  errorsMessage={{
                    required: t("common:required"),
                    validate: t("error:alreadyExisted"),
                    maxLength: t("error:maxLength50"),
                  }}
                />

                <StyledFormItem
                  label={t("contract:exporter.add.common.buyer")}
                  rules={{
                    required: true,
                  }}
                  type="singleSelect"
                  control={control as any}
                  name="buyerId"
                  inputContainerClassName="selector-size-m"
                  inputProps={{
                    suffixIcon: isBuyersFetching ? <Loader /> : undefined,
                    disabled: true,
                    placeholder: t("contract:exporter.add.placeholder.buyer"),
                    filterOption: (input, option) =>
                      ((option?.label as string) ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase()),
                    onChange: async (a: any) => {
                      try {
                        const res = await getBuyerEmployees({
                          id: Number(a),
                        }).unwrap();

                        const buyerCountryCodeItemName =
                          buyerList.find(
                            ({ buyerListQueryResultDto }) =>
                              buyerListQueryResultDto.id.toString() === a
                          )?.buyerListQueryResultDto.countryCodeItemName ??
                          undefined;
                        const buyerCountryInfo = countryList?.find(
                          ({ codeItemName }) =>
                            buyerCountryCodeItemName === codeItemName
                        );
                        const countryLabel = buyerCountryInfo
                          ? `${buyerCountryInfo.codeItemName}) ${buyerCountryInfo.codeItemNameEn}`
                          : undefined;

                        setValue("anyPortIn", countryLabel);
                        setValue(
                          "anyPortInCodeItemName",
                          buyerCountryCodeItemName
                        );

                        const candidate = contractAddBasedOnPoForm;

                        setValue("exporterItemId", candidate["exporterItemId"]);
                        setValue("mainCategory", candidate["mainCategory"]);
                        setValue("subCategory", candidate["subCategory"]);
                        setValue("item", candidate["item"]);
                        setValue(
                          "exporterItemCodeId",
                          candidate["exporterItemCodeId"]
                        );
                        setValue(
                          "exporterItemCode",
                          candidate["exporterItemCode"]
                        );

                        const validBuyerList = res.rows.filter(
                          ({ isActivated, id }) => isActivated
                        );

                        if (validBuyerList.length === 1) {
                          setValue(
                            "buyerEmployeeId",
                            validBuyerList[0].id.toString()
                          );
                          clearErrors("buyerEmployeeId");
                        } else {
                          setValue(
                            "buyerEmployeeId",
                            candidate["buyerEmployeeId"]
                          );
                        }
                      } catch (e: any) {
                        const message = Array.isArray(e.data.message)
                          ? e.data.message[0]
                          : e.data.message;
                        alert.showAlert({ type: "error", message });
                      }
                    },
                  }}
                  options={buyerToOptionList}
                  bottomAccessory={
                    <>
                      <SubFormRow>
                        <SubFormLabel>
                          {t("contract:exporter.add.common.buyerContact")}
                          <strong>*</strong>
                        </SubFormLabel>
                        <Controller
                          name="buyerEmployeeId"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <FlexDiv className="selector-size-s">
                              <StyledSelect
                                {...field}
                                value={field.value || undefined}
                                data-invalid={!!errors.buyerEmployeeId}
                                showSearch
                                filterOption={(input, option) =>
                                  ((option?.label as string) ?? "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                                options={buyerListToOptionList}
                                placeholder={t(
                                  "contract:exporter.add.placeholder.buyerContact"
                                )}
                                suffixIcon={
                                  buyerEmployeesResult.isFetching ? (
                                    <Loader />
                                  ) : undefined
                                }
                                dropdownRender={(menu) => {
                                  return (
                                    <>
                                      <DirectInputSelectOptionButton
                                        onClick={() => {
                                          setValue("buyerEmployeeId", "");
                                          setDialogState(
                                            DialogState.ADD_CONTACT_PERSON
                                          );
                                        }}
                                      >
                                        {t(DIRECT_INPUT_LABEL)}
                                      </DirectInputSelectOptionButton>
                                      {(buyerListToOptionList?.length ?? 0) >
                                        0 && <div>{menu}</div>}
                                    </>
                                  );
                                }}
                              />
                              {errors.buyerEmployeeId && (
                                <StyledInputError
                                  message={t("error:required")}
                                />
                              )}
                            </FlexDiv>
                          )}
                        />
                      </SubFormRow>
                    </>
                  }
                  errorsMessage={{
                    required: t("error:required"),
                    validate: t("error:alreadyExisted"),
                  }}
                />

                <StyledFormItem
                  label={t("common:orderDate")}
                  type="date"
                  control={control as any}
                  name="orderDateAt"
                  rules={{ required: true }}
                  errorsMessage={{
                    required: t("error:required"),
                  }}
                  inputProps={{
                    showToday: false,
                    renderExtraFooter: () => (
                      <DatePickerNoticeTypo
                        text={t("common:datePickerFooterOrderDate")}
                      />
                    ),
                  }}
                />
                <StyledFormItem
                  label={t("contract:exporter.add.common.thirdPartyTrade")}
                  type="checkbox"
                  control={control as any}
                  name="isThirdPartyTrade"
                  inputProps={{
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      if (!e.target.checked) {
                        unregister("allocation");
                      }
                    },
                  }}
                  options={[
                    {
                      label: t(
                        "contract:exporter.add.common.thirdPartyTradeLabel"
                      ),
                      value: "true",
                    },
                  ]}
                  bottomAccessory={
                    <>
                      {!!watch("isThirdPartyTrade").length && (
                        <SubFormRow>
                          <SubFormLabel>
                            {t("common:allocation")}
                            <strong>*</strong>
                          </SubFormLabel>
                          <FlexDiv>
                            <StyledInput
                              {...register("allocation", {
                                required: true,
                              })}
                              placeholder={t("placeholder:enterAllocation")}
                              data-invalid={!!errors.allocation}
                              onClear={() => setValue("allocation", "")}
                            />
                            {!!errors.allocation && (
                              <StyledInputError message={t("error:required")} />
                            )}
                          </FlexDiv>
                        </SubFormRow>
                      )}
                    </>
                  }
                />
                <SectionCardRow
                  label={t("contract:exporter.add.common.optional")}
                  value={optionalItemValue()}
                />
              </HalfFormItemContainer>

              <HalfFormItemContainer>
                <CustomFormItemRow>
                  <StyledCustomLabel>
                    {t("contract:exporter.add.common.item")}
                  </StyledCustomLabel>
                  <DoubleInputContainerWithError style={{ flex: 1 }}>
                    <DoubleInputContainer style={{ width: "100%" }}>
                      <Controller
                        name="exporterItemId"
                        rules={{ required: true }}
                        control={control}
                        render={({ field }) => {
                          const isItemFieldDisabled =
                            isItemsFetching || !watch("buyerId");
                          return (
                            <>
                              <StyledSelect
                                {...field}
                                value={field.value || undefined}
                                style={{ flex: 1, width: 1 }}
                                placeholder={t(
                                  "contract:exporter.add.placeholder.item"
                                )}
                                filterOption={(input, option) =>
                                  ((option?.label as string) ?? "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                                options={itemListToGroupedOptionList}
                                data-invalid={!!errors.exporterItemId}
                                showSearch
                                suffixIcon={
                                  isItemsFetching ? <Loader /> : undefined
                                }
                                disabled={isItemFieldDisabled}
                                onChange={(value) => {
                                  const splitedValue = (value as string).split(
                                    "_"
                                  );
                                  const selectItem = itemsList.find(
                                    (itemInfo) =>
                                      itemInfo.id === Number(splitedValue[0])
                                  );

                                  if (selectItem) {
                                    setValue("exporterItemId", value);
                                    setValue(
                                      "mainCategory",
                                      selectItem.mainCategory
                                    );
                                    setValue(
                                      "subCategory",
                                      selectItem.subCategory
                                    );
                                    setValue("item", selectItem.item);
                                    setValue(
                                      "exporterItemCodeId",
                                      splitedValue[0]
                                    );
                                    setValue(
                                      "exporterItemCode",
                                      selectItem.itemCode
                                    );
                                  }
                                }}
                              />
                              <StyledButton
                                buttonColor="black"
                                buttonGrade="tertiary"
                                buttonSize={40}
                                onClick={() =>
                                  setDialogState(DialogState.ADD_ITEM)
                                }
                                disabled={isItemFieldDisabled}
                              >
                                <AddIcon disabled={isItemFieldDisabled} />{" "}
                                {t("contract:exporter.add.common.addItem")}
                              </StyledButton>
                            </>
                          );
                        }}
                      />
                    </DoubleInputContainer>
                    {errors.exporterItemId && (
                      <InputError message={t("error:required")} />
                    )}
                    {watch("mainCategory") && renderItemSubInputs()}
                  </DoubleInputContainerWithError>
                </CustomFormItemRow>
                <StyledFormItem
                  label={t("contract:exporter.add.common.hsCode")}
                  control={control as any}
                  name="hsCode"
                  type="text"
                  inputProps={{
                    placeholder: t("contract:exporter.add.placeholder.hsCode"),
                  }}
                />

                <Divider />

                <StyledFormItem
                  label={t("contract:exporter.add.common.optional")}
                  type="checkbox"
                  control={control as any}
                  name="isAddBankDetail"
                  options={[
                    {
                      label: t("contract:exporter.add.common.addBankDetail"),
                      value: "true",
                    },
                  ]}
                />
              </HalfFormItemContainer>
            </FlexRow>

            {isVisibleTransactionInformation && <Divider />}
            {isVisibleTransactionInformation && (
              <Typo typoType="b9m" color="gray7">
                {t("contract:exporter.add.common.transactionInformation")}
              </Typo>
            )}

            <FlexRow>
              <HalfFormItemContainer>
                {!hasLastShipmentDateAtOnPo && (
                  <StyledFormItem
                    label={t("contract:exporter.add.common.lastShipmentDate")}
                    type="date"
                    control={control as any}
                    name="lastShipmentDateAt"
                    inputProps={{
                      allowClear: true,
                      showToday: false,
                      renderExtraFooter: () => (
                        <DatePickerNoticeTypo
                          text={t("common:datePickerFooterLastShipmentDate")}
                        />
                      ),
                    }}
                  />
                )}
                {!hasFreetimeOnPo && (
                  <StyledFormItem
                    label={t("contract:exporter.add.common.freetime")}
                    type="singleSelect"
                    name={"freetimeCodeItemName"}
                    control={control as any}
                    options={[
                      {
                        label: t(DIRECT_INPUT_LABEL),
                        value: DIRECT_INPUT_VALUE,
                      },
                      ...freetimeOptions,
                    ]}
                    inputContainerClassName="selector-size-m"
                    inputProps={{
                      placeholder: t(
                        "contract:exporter.add.placeholder.freetime"
                      ),
                      virtual: false,
                      allowClear: true,
                      suffixIcon: isCommonCodeFetching ? <Loader /> : undefined,
                      filterOption: (input, option) =>
                        ((option?.label as string) ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase()) ||
                        ((option?.value as string) ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase()) ||
                        option?.value === DIRECT_INPUT_VALUE,
                      dropdownRender: (menu) => {
                        return (
                          <CustomDropdownWrapper>{menu}</CustomDropdownWrapper>
                        );
                      },
                      onChange: (label: any, option: any) => {
                        if (option) {
                          setValue("freetime", option.label);
                        }
                      },
                      onClear: () => {
                        setValue("freetime", undefined);
                      },
                    }}
                    bottomAccessory={
                      watch("freetimeCodeItemName") === DIRECT_INPUT_VALUE ? (
                        <Input
                          {...register("freetimeDirect")}
                          placeholder={t("placeholder:enterFreetime")}
                          onClear={() => setValue("freetimeDirect", "")}
                        />
                      ) : null
                    }
                  />
                )}
              </HalfFormItemContainer>
              <HalfFormItemContainer>
                {!hasOriginOnPo && (
                  <StyledFormItem
                    label={t("common:originLocation")}
                    type="text"
                    control={control as any}
                    name="origin"
                    inputProps={{
                      placeholder: t(
                        "contract:exporter.add.placeholder.origin"
                      ),
                    }}
                  />
                )}
              </HalfFormItemContainer>
            </FlexRow>
          </FlexColumn>
        </SectionCard>
      </>
    );
  };

  // 첨부 파일
  const renderAttachmentFile = () => {
    return (
      <StyledSectionCard
        cardTitle={t("contract:exporter.add.common.attachmentFile")}
      >
        <FlexGapDiv>
          <StyledFormItem
            label={t("contract:exporter.add.common.poFile")}
            type="file"
            name="poMediaId"
            control={control as any}
            inputProps={{
              defaultFileList: watch("poMediaId")
                ? [
                    new File(
                      [""],
                      watch("poMediaId")?.[0]?.originalFileName ?? ""
                    ),
                  ]
                : undefined,
              onRemove: () => setValue("poMediaId", undefined),
            }}
          />
          <StyledFormItem
            label={t("contract:exporter.add.common.scFile")}
            type="file"
            name="scMediaId"
            control={control as any}
            inputProps={{
              defaultFileList: watch("scMediaId")
                ? [
                    new File(
                      [""],
                      watch("scMediaId")?.[0]?.originalFileName ?? ""
                    ),
                  ]
                : undefined,
              onRemove: () => setValue("scMediaId", undefined),
            }}
          />
          <StyledFormItem
            label={t("contract:exporter.add.common.referenceImage")}
            type="imageUpload"
            name="referenceMediaIdList"
            control={control as any}
            errorsMessage={{
              required: t("error:required"),
            }}
            bottomAccessory={
              <WarningDescription>
                <StyledIcon iconSrc={InfoSvg} iconSize={16} />
                <Typo typoType="b9r" color="gray6">
                  {t("contract:exporter.add.placeholder.referenceImage")}
                </Typo>
              </WarningDescription>
            }
            inputProps={{
              imageSrcList: watch("referenceMediaIdList")?.map(
                ({ mediaUrl }) => {
                  return {
                    imageSrc: mediaUrl,
                    onClick: () => handleImageClick(mediaUrl),
                  };
                }
              ),
              onRemove: (removeItemUrl) => {
                setValue(
                  "referenceMediaIdList",
                  watch("referenceMediaIdList").filter(
                    ({ mediaUrl }) => mediaUrl !== removeItemUrl
                  )
                );
              },
            }}
          />
        </FlexGapDiv>
      </StyledSectionCard>
    );
  };

  // Remark
  const renderContractRemark = () => {
    return (
      <StyledSectionCard
        cardTitle={t("contract:exporter.add.common.contractRemark")}
      >
        <FormItem
          type="textarea"
          name="contractRemark"
          control={control}
          inputProps={{
            style: { width: "100%" },
            placeholder: t("contract:exporter.add.placeholder.contractRemark"),
            maxLength: 1000,
          }}
          noLabel
        />
      </StyledSectionCard>
    );
  };

  const renderDialog = () => {
    if (dialogState === DialogState.IMAGE && selectImage) {
      return (
        <ReferenceImageDialog
          open
          onOpenChange={() => setDialogState(DialogState.NULL)}
          imageList={referenceImageList ?? [""]}
          selectImage={selectImage}
          onChangeSelect={setSelectImage}
        />
      );
    }
    // 아이템 추가
    if (dialogState === DialogState.ADD_ITEM) {
      return (
        <AddItemDialog
          open
          onOpenChange={() => setDialogState(DialogState.NULL)}
          isSingleItemCode
          onSave={handleAddItems}
          allowedMainCategories={buyerMainCategory}
        />
      );
    }
    // 거래처 추가
    if (dialogState === DialogState.ADD_CONTACT_PERSON) {
      const selectedBuyer = buyerList.find(
        ({ id }) => watch("buyerId") === id.toString()
      );

      return (
        <BuyerEmployeeAddDialog
          onSave={handleAddBuyerEmployee}
          onClose={() => setDialogState(DialogState.NULL)}
          defaultCountryCode={selectedBuyer?.telPrefix}
        />
      );
    }
    // 약관추가
    if (dialogState === DialogState.CREATE_TERMS) {
      return (
        <TermsAddDialog
          title={t("common:registerContractTerms")}
          open
          onOpenChange={() => setDialogState(DialogState.NULL)}
          onSave={async () => await exportersData.refetch()}
        />
      );
    }
    // 서명추가
    if (dialogState === DialogState.REGISTER_SIGN) {
      return (
        <RegisterCompanySignDialog
          onOpenChange={() => setDialogState(DialogState.NULL)}
        />
      );
    }
  };

  const renderAlertDialog = () => {
    const closeAlertDialog = () => setAlertDialogState(AlertDialogState.NULL);
    // 뒤로가기
    if (alertDialogState === AlertDialogState.BACK_TO_PREVIOUS) {
      return (
        <AlertDialog
          open
          title={t("contract:exporter.add.alertDialog.backToPrevious.title")}
          onOpenChange={() => {
            closeAlertDialog();
          }}
          onOk={() => navigate(-1)}
          okText={t("contract:exporter.add.common.ok")}
          cancelText={t("contract:exporter.add.common.exit")}
        >
          {t("contract:exporter.add.alertDialog.backToPrevious.description")}
        </AlertDialog>
      );
    }
    // 서명등록
    if (alertDialogState === AlertDialogState.REGISTER_SIGN) {
      return (
        <NoRegisteredSignatureAlertDialog
          open
          onOpenChange={() => setAlertDialogState(AlertDialogState.NULL)}
          onOk={() => {
            setDialogState(DialogState.REGISTER_SIGN);
            closeAlertDialog();
          }}
          onCancel={closeAlertDialog}
        />
      );
    }
    // 서명등록 요청
    if (alertDialogState === AlertDialogState.SIGNATURE_REQUEST) {
      return (
        <SignatureRequestAlertDialog
          open
          onOpenChange={() => closeAlertDialog()}
          onCancel={closeAlertDialog}
        />
      );
    }
    // SC서명선택 (서명 2개일 경우)
    if (alertDialogState === AlertDialogState.SIGNATURE_SELECT_SC) {
      return (
        <SignatureSelectDialog
          open
          onSave={(selectSignatureMediaId) => {
            createScContract(getValues(), selectSignatureMediaId);
            closeAlertDialog();
          }}
          onOpenChange={closeAlertDialog}
        />
      );
    }
  };

  useEffect(() => {
    (async () => {
      try {
        handleContentLoadingOn();
        const result = await getContractDetail({ id });

        if (result.isSuccess) {
          reset({
            scNo: result.data.scNo,
            buyerId: result.data.buyerId.toString(),
            orderDateAt: dayjs(), // 오늘 날짜로 설정
            hsCode: result.data.hsCode || undefined,
            allocation: result.data.allocation || undefined,
            freetime: result.data.freetime || undefined,
            freetimeCodeItemName:
              !result.data.freetimeCodeItemName && !!result.data.freetime
                ? DIRECT_INPUT_VALUE
                : result.data.freetimeCodeItemName,
            freetimeDirect:
              !result.data.freetimeCodeItemName && !!result.data.freetime
                ? result.data.freetime
                : undefined,
            lastShipmentDateAt: result.data.lastShipmentDateAt
              ? dayjs(result.data.lastShipmentDateAt)
              : undefined,
            paymentTerms: result.data.paymentTerms || undefined,
            paymentTermsCodeItemName:
              result.data.paymentTermsCodeItemName || undefined,
            paymentTermsRemark: result.data.paymentTermsRemark || undefined,
            isThirdPartyTrade: result.data.allocation ? ["true"] : [],
            isAddBankDetail: result.data.isAddBankDetail ? ["true"] : [],
            isAddContractTerms: result.data.isAddContractTerms ? ["true"] : [],
            referenceMediaIdList: result.data.referenceSimpleMedias ?? [],
            origin: result.data.origin || undefined,
          });

          await getBuyerEmployees({ id: result.data.buyerId });
        }
      } catch (e: any) {
        const message = Array.isArray(e.data.message)
          ? e.date.message[0]
          : e.date.message;
        alert.showAlert({ message, type: "error" });
      } finally {
        handleContentLoadingOff();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ExporterMainLayout
      breadcrumb={[t("sideNav:contract"), t("sideNav:exportContractCreate")]}
      customPageTitle={
        <HeaderInnerDiv>
          <h1>{t("sideNav:exportContractCreate")}</h1>
        </HeaderInnerDiv>
      }
      pageTitle={t("sideNav:exportContractCreate")}
    >
      <ContentSection>
        <StyledForm onSubmit={handleSubmit(handleCreateContract)}>
          <WarningDescription>
            <Icon iconSrc={InfoSvg} iconSize={16} />
            {t("contract:exporter.add.contractAddInfo")}
          </WarningDescription>

          {renderContractInfoBasedPo()}
          {renderAdditionalInfoBasedPo()}

          <Section>
            {renderAttachmentFile()}
            {renderContractRemark()}
          </Section>

          <BottomFixedContainer>
            <FooterButtonSection>
              <Button
                buttonGrade="tertiary"
                buttonColor="black"
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
                onClick={() =>
                  setAlertDialogState(AlertDialogState.BACK_TO_PREVIOUS)
                }
              >
                <Icon iconSrc={ChevronLeftSvg} iconSize={16} />{" "}
                {t("contract:exporter.add.buttonLabel.backToPrevious")}
              </Button>

              <SaveButtonContainer>
                <Button
                  ref={previewButtonRef}
                  onClick={async () => {
                    previewScPdfResourceRef.current = getValues();
                    setIsPreviewLoading(true);
                    try {
                      await handlePreview();
                    } catch (error) {
                      console.error("PDF 미리보기 실패:", error);
                    } finally {
                      setIsPreviewLoading(false);
                    }
                  }}
                  buttonGrade="tertiary"
                  buttonColor="black"
                  isLoading={exportersData.isFetching || isPreviewLoading}
                  disabled={exportersData.isFetching || isPreviewLoading}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <Icon iconSrc={PreviewSvg} iconSize={16} />
                  {t("contract:exporter.add.buttonLabel.preview")}
                </Button>

                <Button
                  buttonGrade="primary"
                  style={{ width: 158 }}
                  type="submit"
                  disabled={isCreateDisabled}
                >
                  {t("contract:exporter.add.buttonLabel.save")}
                </Button>
              </SaveButtonContainer>
            </FooterButtonSection>
          </BottomFixedContainer>
        </StyledForm>
      </ContentSection>

      {renderDialog()}
      {renderAlertDialog()}
    </ExporterMainLayout>
  );
};

export default ContractAddBasedOnPoPage;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SaveButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const StyleButton = styled(Button)`
  ${typo.btn3m}
`;

const HeaderInnerDiv = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 0;

  h1 {
    ${typo.h1}
  }

  ${StyleButton} {
    flex-shrink: 0;
    margin-left: auto;
  }
`;

const WarningDescription = styled.p`
  ${typo.b9r};
  color: ${colorSet.gray4};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const HalfFormItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(50% - 12px);
  gap: 24px;
`;

const SubFormLabel = styled.p`
  color: ${colorSet.gray6};
  ${typo.b9r};

  strong {
    color: ${colorSet.red2};
    font-size: 12px;
    padding-left: 2px;
  }
`;

const FlexDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  &.selector-size-s {
    width: calc(100% - 164px - 16px - 100px);
  }
`;

const SubFormRow = styled.div<{ isRequired?: boolean }>`
  display: flex;
  width: 100%;
  gap: 8px;

  ${FlexDiv} {
    flex: 1;
  }

  ${SubFormLabel} {
    width: 100px;
    flex-shrink: 0;
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;

  &[data-invalid="true"] {
    .ant-select-selector {
      border: 1px solid ${colorSet.red2} !important;
    }
  }

  .ant-select-selection-placeholder {
    width: 1px;
  }
`;

const CustomLabel = styled.p`
  width: 164px;
  color: ${colorSet.gray6};
  ${typo.b9r};
  flex-shrink: 0;

  &::after {
    content: " *";
    color: ${colorSet.red2};
  }
`;

const StyledCustomLabel = styled(CustomLabel)`
  color: ${colorSet.gray1};
  ${typo.b7r};
`;

const CustomFormItemRow = styled.div`
  display: flex;
  gap: 8px;

  ${mediaQuery.FORM_INPUT_TO_FULL_INPUT} {
    flex-direction: column;
  }
`;

const DoubleInputContainer = styled.div`
  display: flex;
  gap: 8px;
  flex: 1;

  &.fixed-width-container {
    width: calc(100% - 172px);

    ${mediaQuery.FORM_INPUT_TO_FULL_INPUT} {
      width: 100%;
    }
  }
`;

const StyledSectionCard = styled(SectionCard)`
  flex: 1;
`;

const Section = styled.section`
  display: flex;
  gap: 16px;
`;

const FlexGapDiv = styled(FlexDiv)`
  gap: 24px;
`;

const ContentSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 72px;
`;

const StyledInputError = styled(InputError)`
  padding-top: 8px;
`;

const DoubleInputContainerWithError = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledInput = styled(Input)`
  width: 100%;
`;

const FooterButtonSection = styled.section`
  display: flex;
  justify-content: space-between;
`;

const StyledFormItem = styled(FormItem)`
  .ant-select-selection-placeholder {
    width: 1px;
  }

  .selector-size-m {
    width: calc(100% - 164px - 8px);
  }

  .ant-picker .ant-picker-clear {
    width: 18px;
    height: 18px;
    outline: 1px solid white;
  }

  ${mediaQuery.FORM_INPUT_TO_FULL_INPUT} {
    flex-direction: column;
    gap: 8px;

    .selector-size-m {
      width: 100%;
    }
  }
`;

const StyledIcon = styled(Icon)`
  flex-shrink: 0;
`;

const AddIcon = styled(AddSvg)<{ disabled?: boolean }>`
  width: 16px;
  height: 16px;

  path {
    ${({ disabled }) =>
      disabled &&
      css`
        fill: ${colorSet.gray8};
      `};
  }
`;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex-shrink: 0;
`;

const CustomDropdownWrapper = styled.div`
  div.ant-select-item {
    &:first-of-type {
      color: ${colorSet.blue4};

      &::before {
        content: url(${AddBlueSvg});
        width: 18px;
        height: 18px;
        position: relative;
        margin-right: 8px;
        top: 4px;
      }
    }
  }
`;

const StyledSectionCardRow = styled(SectionCardRow)`
  &[data-columns="3"] {
    grid-column: 3 span;
  }

  &[data-columns="4"] {
    grid-column: 4 span;
  }

  &[data-columns="6"] {
    grid-column: 6 span;
  }

  &[data-columns="12"] {
    grid-column: 12 span;
  }

  &[data-divider="true"] {
    border-top: 1px solid ${colorSet.gray9};
  }
`;

const BuyerText = styled(Typo)`
  text-decoration-line: underline;
  text-underline-position: under;
  text-decoration-thickness: 2px;
  cursor: pointer;
  word-break: break-all;
`;

const BottomSectionCard = styled(SectionCardGrid)`
  padding: 16px 24px;
  background: ${colorSet.gray11};
  border: 1px solid ${colorSet.gray10};
  text-align: center;
`;

const StyledSectionCardCenterRow = styled(StyledSectionCardRow)`
  align-items: center;

  .ant-typography {
    word-break: break-all;
    white-space: pre-wrap;
  }
`;

const FlexColumn = styled.div<{ flexGap?: number }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ flexGap }) =>
    flexGap &&
    css`
      gap: ${flexGap}px;
    `};
`;

const FlexRow = styled.div`
  display: flex;
  width: 100%;
  gap: 24px;
`;

const Divider = styled.div`
  border-top: 1px solid ${colorSet.gray9};
  margin: 24px 0;
`;

const DirectInputSelectOptionButton = styled(SelectOptionButton)`
  color: ${colorSet.blue4};

  &:hover {
    &&& {
      color: ${colorSet.blue4};
    }
  }

  &::before {
    content: url(${AddBlueSvg});
    width: 18px;
    height: 18px;
    position: relative;
    margin-right: 8px;
    top: 4px;
  }
`;
